import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface CommandPaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** Open with this text already typed (a list page's "Search everything"). */
  openWithQuery: (query: string) => void;
  /** Read once by the palette when it opens, then cleared. */
  initialQuery: string;
}

const CommandPaletteContext = createContext<CommandPaletteState | null>(null);

/**
 * Hosts the open/closed state for the global command palette, plus the document-level
 * Cmd/Ctrl+K listener. Mounted once near the top of the app tree so every descendant
 * can trigger the palette via `useCommandPalette()` and so the keyboard shortcut works
 * regardless of which element has focus. Does NOT render the palette UI itself —
 * `<CommandPalette />` is mounted separately in `Layout.tsx`.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');

  const setOpen = useCallback((next: boolean) => {
    if (!next) setInitialQuery('');
    setOpenState(next);
  }, []);
  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);
  const openWithQuery = useCallback((query: string) => {
    setInitialQuery(query);
    setOpenState(true);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isToggleShortcut = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (!isToggleShortcut) return;
      // Prevent the browser's location-bar search (Chrome) and anything else listening.
      e.preventDefault();
      toggle();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggle]);

  const value = useMemo<CommandPaletteState>(
    () => ({ open, setOpen, toggle, openWithQuery, initialQuery }),
    [open, setOpen, toggle, openWithQuery, initialQuery],
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}

export function useCommandPalette(): CommandPaletteState {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  }
  return ctx;
}

/** For components that may render outside the provider (a page in a test, a standalone surface). */
export function useOptionalCommandPalette(): CommandPaletteState | null {
  return useContext(CommandPaletteContext);
}
